import { useState, useEffect } from "react";
import * as authClient from "@/app/api/auth-client";

interface ParentOfferBlockProps {
  parentOfferId: string;
  formatCurrency: (value: number) => string;
  formatDate: (date: Date) => string;
  /** Optional label override */
  label?: string;
  /** When provided (e.g. from list root), show total offer value (products + cash) instead of cash only */
  totalOfferValue?: number;
}

/**
 * Fetches and displays the parent (original) offer when viewing a counter offer,
 * so both seller and buyer can track how the negotiation evolved.
 */
export function ParentOfferBlock({
  parentOfferId,
  formatCurrency,
  formatDate,
  label = "Oferta original",
  totalOfferValue,
}: ParentOfferBlockProps) {
  const [parent, setParent] = useState<authClient.ApiOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    authClient
      .getOfferById(parentOfferId)
      .then((offer) => {
        if (!cancelled) setParent(offer);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [parentOfferId]);

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <p className="text-xs text-muted-foreground mb-2">{label}</p>
        <p className="text-sm text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (error || !parent) {
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <p className="text-xs text-muted-foreground mb-2">{label}</p>
        <p className="text-sm text-muted-foreground">Não foi possível carregar a oferta original.</p>
      </div>
    );
  }

  const amountCents = Number(parent.amount_cents);
  const cashValue = amountCents / 100;
  const displayValue = totalOfferValue != null ? totalOfferValue : cashValue;
  const status =
    parent.status === "declined"
      ? "Recusada"
      : parent.status === "withdrawn"
        ? "Retirada"
        : parent.status === "accepted"
          ? "Aceita"
          : parent.status === "countered"
            ? "Contra-ofertada"
            : "Pendente";

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">
          {totalOfferValue != null ? "Valor total da oferta:" : "Valor (dinheiro):"}
        </span>
        <span className="font-medium">{formatCurrency(displayValue)}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Status:</span>
        <span className="font-medium">{status}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Data:</span>
        <span>{formatDate(new Date(parent.created_at))}</span>
      </div>
      {parent.message?.trim() && (
        <div className="pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground mb-1">Mensagem:</p>
          <p className="text-sm">{parent.message.trim()}</p>
        </div>
      )}
    </div>
  );
}
